import React from 'react';
import { Helmet } from 'react-helmet';
import { ThemeProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { StyledEngineProvider } from '@mui/material/styles';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import CssBaseline from '@mui/material/CssBaseline';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { getMsalConfig } from './auth/config';
import AuthContext from './auth/AuthContext';
import theme from './theme/theme.js';
import store from './app/store';
import Routes from './routes';
import Bootstrap from './app/Bootstrap';
import StaticLoader from './components/mui/MuiLoader/StaticLoader/StaticLoader';
import { initTracking } from './utilities/analytics/analytics';
import './theme/fonts/fonts.css';
import './print.css';

const setMsalInstance = () => {
  const msalInstance = new PublicClientApplication(getMsalConfig());
  // Default to using the first account if no account is active on page load
  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }
  // Optional - This will update account state if a user signs in from another tab or window
  msalInstance.enableAccountStorageEvents();

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  return msalInstance;
};

const App = () => {
  return (
    <>
      <Helmet>
        <html lang="en-GB" />
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
        />
        <title>Guardian1821</title>
      </Helmet>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <StyledEngineProvider injectFirst>
          <Provider store={store}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Bootstrap>
                {({ resolving }) => {
                  if (!resolving) {
                    initTracking();
                    return (
                      <AuthContext.Provider value={setMsalInstance()}>
                        <Routes />
                      </AuthContext.Provider>
                    );
                  }
                  return <StaticLoader />;
                }}
              </Bootstrap>
            </LocalizationProvider>
          </Provider>
        </StyledEngineProvider>
      </ThemeProvider>
    </>
  );
};

export default App;

if (window.Cypress) {
  window.store = store;
}
